import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Badge,
  Typography,
  Paper,
  Chip,
} from '@mui/material';
import {
  Group,
  Feed,
  Person,
  Add,
  Assessment,
  School,
  Explore,
  Forum,
  Favorite,
  TrendingUp,
  EmojiEvents,
  CardMembership,
} from '@mui/icons-material';
import { useAuth } from '../../context/AppContext';
import CollegeService from '../../services/collegeService';

interface NavigationProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

interface NavItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  badge?: number;
  facultyOnly?: boolean;
  studentOnly?: boolean;
}

const Navigation: React.FC<NavigationProps> = ({ mobileOpen, onMobileClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isFaculty, isStudent, isAmbassador, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Student navigation - Social media style
  const studentNavigation: NavItem[] = [
    {
      text: 'Feed',
      icon: <Feed />,
      path: '/feed',
    },
    {
      text: 'Clubs',
      icon: <Group />,
      path: '/clubs',
    },
    {
      text: 'My Profile',
      icon: <Person />,
      path: '/profile',
    },
  ];

  // Faculty navigation - Professional focused
  const facultyNavigation: NavItem[] = [
    {
      text: 'Feed',
      icon: <Feed />,
      path: '/feed',
    },
    {
      text: 'My Clubs',
      icon: <Group />,
      path: '/clubs',
    },
    {
      text: 'Student Approval',
      icon: <School />,
      path: '/student-approval',
      facultyOnly: true,
    },
    {
      text: 'My Profile',
      icon: <Person />,
      path: '/profile',
    },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      onMobileClose();
    }
  };

  const renderNavItem = (item: NavItem) => {
    // Check permissions
    if (item.facultyOnly && !isFaculty) return null;
    if (item.studentOnly && !isStudent) return null;

    const isActive = location.pathname === item.path;

    return (
      <ListItem key={item.text} disablePadding>
        <ListItemButton
          onClick={() => handleNavigation(item.path)}
          selected={isActive}
          sx={{
            borderRadius: 2,
            mx: 1,
            mb: 0.5,
            minHeight: 48,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: isActive ? 'primary.main' : 'action.hover',
              transform: 'translateX(4px)',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            },
            '&.Mui-selected': {
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
              '&:hover': {
                backgroundColor: 'primary.dark',
                transform: 'translateX(4px)',
              },
              '& .MuiListItemIcon-root': {
                color: 'primary.contrastText',
              },
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            {item.badge && item.badge > 0 ? (
              <Badge badgeContent={item.badge} color="error">
                {item.icon}
              </Badge>
            ) : (
              item.icon
            )}
          </ListItemIcon>
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{
              sx: {
                fontSize: '0.875rem',
                fontWeight: isActive ? 600 : 500,
              }
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  const drawerContent = (
    <Box sx={{ overflow: 'auto', py: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* User Profile Section */}
      <Box sx={{ px: 2, mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Box
              component="img"
              src="/logo.png"
              alt="Logo"
              sx={{
                width: 40,
                height: 40,
                mr: 2,
                borderRadius: 1,
                bgcolor: 'white',
                p: 0.5,
              }}
            />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                {user?.name}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
                {CollegeService.getCollegeName()}
              </Typography>
              <Chip
                label={user?.role}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontSize: '0.65rem',
                  height: '18px',
                  mt: 0.5,
                }}
              />
            </Box>
          </Box>
        </Paper>
      </Box>

      <Box sx={{ flexGrow: 1 }}>
        {/* Main navigation based on user role */}
        <Box sx={{ px: 1, mb: 2 }}>
          <Typography
            variant="overline"
            sx={{
              px: 2,
              color: 'text.secondary',
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: 1,
            }}
          >
            {isStudent ? '🌟 Your Space' : '👨‍🏫 Faculty Hub'}
          </Typography>
          <List dense>
            {(isStudent ? studentNavigation : facultyNavigation).map(renderNavItem)}
          </List>
        </Box>
      </Box>

      {/* Footer section for students only (not faculty or ambassadors) */}
      {isStudent && !isAmbassador && !isFaculty && (
        <Box sx={{ px: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <List dense>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => handleNavigation('/apply-ambassador')}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                    transform: 'scale(1.02)',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: 'white' }}>
                  <CardMembership />
                </ListItemIcon>
                <ListItemText
                  primary="Become Ambassador"
                  primaryTypographyProps={{
                    sx: {
                      fontSize: '0.875rem',
                      fontWeight: 600,
                    }
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              textAlign: 'center',
              display: 'block',
              mt: 1,
            }}
          >
            🎯 Join clubs, connect with peers, and grow your network!
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { md: 240 },
        flexShrink: { md: 0 },
      }}
    >
      {/* Mobile drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onMobileClose}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 240,
              borderRight: 'none',
              boxShadow: '2px 0 10px rgba(0, 0, 0, 0.1)',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Desktop drawer */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 240,
              top: '64px', // Account for AppBar height
              height: 'calc(100vh - 64px)',
              borderRight: 'none',
              boxShadow: '2px 0 10px rgba(0, 0, 0, 0.1)',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </Box>
  );
};

export default Navigation;